"use client";

import { CSSProperties, FC, ReactNode, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/useIsMobile";

/** Atlas strip typography — not configurable in CMS */
const HEADER_4_FONT_FAMILY = "var(--font-google-sans)";

function stripHtml(html: string): string {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .trim();
}

/** MySQL has no real BOOLEAN; `TINYINT(1)` / Sequelize often returns 0/1. */
function coerceMysqlBoolean(value: unknown, whenMissing: boolean): boolean {
  if (value == null) return whenMissing;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (value === 0) return false;
    if (value === 1) return true;
    return value !== 0;
  }
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (t === "0" || t === "false" || t === "no" || t === "off" || t === "") return false;
    if (t === "1" || t === "true" || t === "yes" || t === "on") return true;
  }
  return Boolean(value);
}

const Header4DataSchema = z.object({
  header_status: z.number().optional().transform(n => Boolean(n)),
  /** Plain text; every `{...}` is linked to `link_href` (strips HTML for legacy). */
  promotional_text: z.string().optional().default(""),
  promotional_text_color: z.string().optional().default("#FFFFFF"),
  link_color: z.string().optional().default("#FFFFFF"),
  gradient_color_1: z.string().optional().default("#342B7C"),
  gradient_color_2: z.string().optional().default("#00D4EB"),
  background_gradient_angle: z.string().optional().default("90deg"),
  link_href: z.string().optional().default(""),
  link_trailing_image: z.string().optional().default(""),
  link_trailing_image_alt: z.string().optional().default(""),
});

type Header4Data = z.infer<typeof Header4DataSchema>;

/** Up-right (↗) link arrow — Figma’s asset paths read as top-left → bottom-right; this matches outward / CTA direction */
const AtlasDefaultLinkIcon: FC<{ className?: string; color: string }> = ({
  className,
  color,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={18}
    height={18}
    viewBox="0 0 18 18"
    fill="none"
    className={className}
    aria-hidden
  >
    <path
      d="M4.5 13.5L13.5 4.5"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.5 4.5H9.25M13.5 4.5V8.75"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type BraceSegment = { type: "text" | "link"; text: string };

/** Split plain text on `{...}`; each braced subtext is a link segment. */
function parseBraceSegments(plain: string): BraceSegment[] {
  const chunks = plain.split(/(\{[^}]+\})/g);
  const out: BraceSegment[] = [];
  for (const ch of chunks) {
    if (ch === "") continue;
    const m = /^\{([^}]*)\}$/.exec(ch);
    if (m) {
      out.push({ type: "link", text: (m[1] ?? "").trim() });
    } else {
      out.push({ type: "text", text: ch });
    }
  }
  if (out.length === 0) {
    return [{ type: "text", text: plain }];
  }
  return out;
}

const Header4: FC<any> = (props) => {
  const raw = props?.data?.data || props?.data || props;
  const isPreview = Boolean(props?.data?.isPreview ?? props?.isPreview);
  const parseResult = useMemo(() => Header4DataSchema.safeParse(raw), [raw]);
  const isMobile = useIsMobile();

  if (!parseResult.success) {
    console.error("Header4 Zod validation failed:", parseResult.error);
    return null;
  }

  const d: Header4Data = parseResult.data;

  if (!d.header_status) {
    return null;
  }

  const plain = stripHtml(d.promotional_text || "");
  const segments = parseBraceSegments(plain);

  const gradientStyle: CSSProperties = {
    background: `linear-gradient(${d.background_gradient_angle}, ${d.gradient_color_1}, ${d.gradient_color_2})`,
  };

  const mainColor = d.promotional_text_color;
  const linkAccent = d.link_color || mainColor;
  const textStyle: CSSProperties = {
    color: mainColor,
    fontFamily: HEADER_4_FONT_FAMILY,
  };

  const linkStyle: CSSProperties = {
    color: linkAccent,
  };

  const hasLinkTarget = Boolean(d.link_href?.trim());
  const spanTextClass =
    "min-w-0 shrink truncate text-center text-[11px] font-medium leading-tight sm:text-left sm:text-xs md:text-sm";

  const renderLink = (linkLabel: string, key: string | number): ReactNode => {
    if (linkLabel.length === 0) {
      return null;
    }
    if (hasLinkTarget) {
      if (/^https?:\/\//i.test(d.link_href) || d.link_href.startsWith("//")) {
        return (
          <a
            key={key}
            href={d.link_href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold shrink-0 underline decoration-1 underline-offset-2"
            style={linkStyle}
          >
            {linkLabel}
          </a>
        );
      }
      return (
        <Link
          key={key}
          href={d.link_href}
          className="font-semibold shrink-0 underline decoration-1 underline-offset-2"
          style={linkStyle}
        >
          {linkLabel}
        </Link>
      );
    }
    return (
      <span
        key={key}
        className="font-semibold shrink-0 underline decoration-1 underline-offset-2"
        style={linkStyle}
      >
        {linkLabel}
      </span>
    );
  };

  const renderLinkTrailing = () =>
    d.link_trailing_image && d.link_trailing_image.trim() !== "" ? (
      <Image
        src={d.link_trailing_image}
        alt={d.link_trailing_image_alt || ""}
        width={18}
        height={18}
        className="shrink-0 object-contain"
      />
    ) : (
      <AtlasDefaultLinkIcon className="shrink-0" color={linkAccent} />
    );

  return (
    <div
      className={cn(
        "flex w-full items-center justify-center",
        "gap-2 px-4 py-2.5 sm:px-6 sm:py-2 md:px-8 md:py-2.5 lg:px-12",
        isPreview ? "relative" : "sticky top-0 z-50",
      )}
      style={gradientStyle}
    >
      <div
        className={cn(
          "flex min-w-0 max-w-full flex-1 items-center justify-center gap-1.5 sm:gap-2",
          isMobile
            ? "max-w-[36ch] truncate whitespace-nowrap"
            : "sm:whitespace-nowrap",
        )}
        style={textStyle}
        title={plain || undefined}
      >
        {segments.map((seg, i) => {
          if (seg.type === "text") {
            if (!seg.text) {
              return null;
            }
            return (
              <span key={i} className={spanTextClass}>
                {seg.text}
              </span>
            );
          }
          return (
            <span key={i} className="inline-flex shrink-0 items-center gap-0.5">
              {renderLink(seg.text, `link-${i}`)}
              {renderLinkTrailing()}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default Header4;
